<template>
  <div id="app">
    <a
      class="skip-link"
      href="#main"
    >Saltar al contenido principal</a>

    <header id="unc-header">
      <div class="header-inner">
        <button
          class="menu-trigger"
          :class="{ active: mobileOpen }"
          type="button"
          aria-label="Menú"
          aria-controls="mobile-menu"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          @click="toggleMobileMenu()"
        >
          <span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
        </button>

        <div class="logo">
          <router-link
            to="/"
            class="logo-link"
          >
            <img
              src="./assets/img/Logo-UNC-azul.png"
              alt="Usuario UNC — Ir al inicio"
            >
          </router-link>
        </div>

        <div class="header-actions">
          <nav aria-label="Principal">
            <ul class="nav-links">
              <li>
                <router-link
                  to="/"
                  :class="{ active: $route.path === '/' }"
                  :aria-current="$route.path === '/' ? 'page' : null"
                >Inicio</router-link>
              </li>
              <li>
                <router-link
                  to="/contactos"
                  :class="{ active: $route.path === '/contactos' }"
                  :aria-current="$route.path === '/contactos' ? 'page' : null"
                >Contactos</router-link>
              </li>
              <li>
                <router-link
                  to="/preguntas-frecuentes"
                  :class="{ active: $route.path === '/preguntas-frecuentes' }"
                  :aria-current="$route.path === '/preguntas-frecuentes' ? 'page' : null"
                >Preguntas frecuentes</router-link>
              </li>
            </ul>
          </nav>

          <div
            class="nav-divider"
            aria-hidden="true"
          />

          <button
            class="btn-correo"
            type="button"
            @click="uncEmail()"
          >
            Correo UNC
            <span class="visually-hidden">(se abre en una pestaña nueva)</span>
          </button>
          <button
            class="btn-miunc"
            type="button"
            @click="usersProfile()"
          >
            MiUNC
            <span class="visually-hidden">(se abre en una pestaña nueva)</span>
          </button>
        </div>
      </div>

      <nav
        id="mobile-menu"
        ref="mobileMenu"
        class="mobile-menu"
        :class="{ open: mobileOpen }"
        aria-label="Principal (móvil)"
      >
        <router-link
          to="/"
          :aria-current="$route.path === '/' ? 'page' : null"
          @click.native="closeMobileMenu()"
        >Inicio</router-link>
        <router-link
          to="/contactos"
          :aria-current="$route.path === '/contactos' ? 'page' : null"
          @click.native="closeMobileMenu()"
        >Contactos</router-link>
        <router-link
          to="/preguntas-frecuentes"
          :aria-current="$route.path === '/preguntas-frecuentes' ? 'page' : null"
          @click.native="closeMobileMenu()"
        >Preguntas frecuentes</router-link>
        <button
          type="button"
          @click="uncEmail(); closeMobileMenu()"
        >
          Correo UNC
          <span class="visually-hidden">(se abre en una pestaña nueva)</span>
        </button>
        <button
          class="m-btn-miunc"
          type="button"
          @click="usersProfile(); closeMobileMenu()"
        >
          MiUNC
          <span class="visually-hidden">(se abre en una pestaña nueva)</span>
        </button>
      </nav>
    </header>

    <main
      id="main"
      tabindex="-1"
    >
      <section id="content">
        <div id="main-body">
          <router-view />
        </div>
      </section>
    </main>

    <footer id="footer">
      <img
        src="@/assets/img/fondosUNC/unc-logo-footer.png"
        height="60px"
        alt="Universidad Nacional de Córdoba — Prosecretaría de Informática"
      >
    </footer>

    <asistente-widget v-if="configApp && configApp.asistenteEnabled" />
  </div>
</template>

<script>
import { HttpFRONT, swal } from './share.js';
import { mapGetters } from 'vuex';
import AsistenteWidget from './components/asistente/asistente-widget.vue';

export default {
    name: 'App',
    components: {
        AsistenteWidget,
    },
    data () {
        return {
            mobileOpen: false,
            resizeHandler: null,
            keydownHandler: null,
        };
    },
    computed: {
        ...mapGetters([
            'configApp',
        ]),
        isInitStatus: function () {
            return this.$store.state.isInitStatus;
        },
    },
    watch: {
        // Foco al contenido al navegar, para lectores de pantalla (no en la carga inicial ni en cambios de hash).
        $route (to, from) {
            if (to.path === from.path || !from.matched.length) return;

            this.$nextTick(() => {
                document.getElementById('main')?.focus({ preventScroll: true });
            });
        },
    },
    mounted () {
        this.resizeHandler = () => {
            if (window.innerWidth > 820 && this.mobileOpen) {
                this.closeMobileMenu();
            }
        };

        this.keydownHandler = event => {
            if (event.key === 'Escape' && this.mobileOpen) {
                this.closeMobileMenu({ restoreFocus: true });
            }
        };

        window.addEventListener('resize', this.resizeHandler, { passive: true });
        window.addEventListener('keydown', this.keydownHandler);
        this.resizeHandler();
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('keydown', this.keydownHandler);
    },
    created () {
        this.$store.dispatch('InitStatus');
    },
    methods: {
        toggleMobileMenu () {
            this.mobileOpen = !this.mobileOpen;

            if (this.mobileOpen) {
                this.$nextTick(() => {
                    const firstLink = this.$refs.mobileMenu?.querySelector('a, button');
                    firstLink?.focus();
                });
            }
        },
        closeMobileMenu ({ restoreFocus = false } = {}) {
            if (!this.mobileOpen) return;

            this.mobileOpen = false;

            if (restoreFocus) {
                this.$nextTick(() => {
                    this.$el.querySelector('.menu-trigger')?.focus();
                });
            }
        },
        openInNewTab (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        },
        async usersProfile () {
            try {
                const response = await HttpFRONT.get('miuncurl');
                if (response.status === 200) {
                    this.openInNewTab(response.data.url);
                }
            } catch (error) {
                const status = error.response.status;
                const message =
                    (error.response && error.response.data) ||
                    'Servicio temporalmente no disponible';

                switch (status) {
                case 401:
                    swal({
                        title: 'Acceso denegado',
                        text: 'No posee permisos para acceder a este servicio.',
                        type: 'warning',
                    });
                    break;
                case 500:
                    swal({
                        title: 'Algo salió mal',
                        text: message,
                        type: 'warning',
                    });
                    break;
                default:
                    swal({
                        title: 'Datos no guardados',
                        text: message,
                        type: 'warning',
                    });
                    break;
                }
            }
        },
        uncEmail () {
            this.openInNewTab(this.configApp.urlUncEmail);
        },
    },
};

</script>

<style lang="scss">
body {
  padding-top: 0 !important;
}

.skip-link {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 200;
  padding: 10px 18px;
  border-radius: 0 0 8px 0;
  background: #003865;
  color: #fff;
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.skip-link:focus {
  left: 0;
}

.header-actions > nav {
  display: flex;
  align-items: center;
}

#unc-header {
  --focus-ring: var(--focus-ring-on-dark);
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  background: rgba(0, 30, 60, 0.95);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.18);
}

#footer {
  padding-bottom: 50px !important;
}

.header-inner {
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 0px 20px !important;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 0;
  list-style: none;
  justify-content: space-between;
  margin-block: 0;
  padding-inline-start: 28px;
}

.header-actions {
  display: flex;
  align-items: center;
  margin-left: 24px;
}

.logo {
  margin-right: auto;
  margin-top: 20px;
  filter: brightness(0) invert(1);
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 10px;
  padding: 0 !important;
}

.logo-link img {
  height: 48px;
  width: 150px !important;
  object-fit: contain;
  margin-left: 0 !important;
  transform: translateX(-20px);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
  list-style: none;
  margin-right: 12px;
  margin-bottom: 0;
}

.nav-links a {
  position: relative;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.25s ease;
}

.nav-links a:link,
.nav-links a:visited,
.nav-links a:active {
  color: rgba(255, 255, 255, 0.65);
}

.nav-links a:visited {
  color: rgba(255, 255, 255, 0.65);
}

.nav-links a::after {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 12px;
  right: 12px;
  height: 1.5px;
  background: #3eb6b8;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.nav-links a:hover {
  color: white;
  background: rgba(255, 255, 255, 0.06);
}

.nav-links a.active {
  color: white;
  background: rgba(255, 255, 255, 0.06);
}

.nav-links a:hover::after {
  transform: scaleX(1);
}

.nav-links a.active::after {
  transform: scaleX(1);
}

.nav-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.12);
  margin: 0px;
  margin-right: 20px;
}

.btn-correo {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  transition: all 0.25s ease;
  letter-spacing: 0.3px;
}

.btn-correo:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-color: rgba(255, 255, 255, 0.35);
}

.btn-miunc {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 100px;
  border: none;
  background: white;
  color: #003865;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  letter-spacing: 0.3px;
  margin-left: 8px;
  position: relative;
  overflow: hidden;
}

.btn-miunc::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(62, 182, 184, 0.15), transparent);
  opacity: 0;
  transition: opacity 0.3s;
}

.btn-miunc:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.btn-miunc:hover::before {
  opacity: 1;
}

.menu-trigger {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  margin-right: 12px;
  transition: background 0.2s;
}

.menu-trigger:hover {
  background: rgba(255, 255, 255, 0.08);
}

.menu-trigger span {
  display: block;
  width: 20px;
  height: 1.5px;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.menu-trigger.active span:nth-child(1) {
  transform: translateY(6.5px) rotate(45deg);
}

.menu-trigger.active span:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.menu-trigger.active span:nth-child(3) {
  transform: translateY(-6.5px) rotate(-45deg);
}

.mobile-menu {
  display: none;
  position: absolute;
  top: 64px;
  left: 0;
  right: 0;
  z-index: 101;
  flex-direction: column;
  gap: 4px;
  padding: 12px 20px 20px;
  background: rgba(0, 30, 60, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2);
}

.mobile-menu.open {
  display: none;
}

.mobile-menu a,
.mobile-menu button {
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 12px 8px;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.mobile-menu a:link,
.mobile-menu a:visited,
.mobile-menu a:active {
  color: rgba(255, 255, 255, 0.7);
}

.mobile-menu a:visited {
  color: rgba(255, 255, 255, 0.7);
}

.mobile-menu a:hover,
.mobile-menu button:not(.m-btn-miunc):hover {
  color: white;
}

.mobile-menu .m-btn-miunc {
  margin-top: 12px;
  background: white;
  color: #003865;
  border-radius: 100px;
  text-align: center;
  padding: 12px;
  font-weight: 600;
  border-bottom: none;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.mobile-menu .m-btn-miunc:hover {
  color: #003865;
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
}

@media (max-width: 900px) {
  .header-inner {
    gap: 0;
  }

  .nav-links {
    gap: 2px;
  }

  .nav-links a {
    padding: 6px 10px;
    font-size: 12px;
  }

  .btn-correo {
    font-size: 12px;
    padding: 7px 12px;
  }

  .btn-miunc {
    font-size: 12px;
    padding: 7px 16px;
    margin-left: 6px;
  }

  .nav-divider {
    margin-right: 12px;
  }
}

@media (max-width: 848px) {
  .header-actions {
    margin-left: auto;
  }

  .nav-links,
  .nav-divider,
  .btn-correo,
  .btn-miunc {
    display: none !important;
  }

  .menu-trigger {
    display: flex;
  }

  .mobile-menu.open {
    display: flex;
  }

  #unc-header,
  .mobile-menu {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}
</style>
